import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../lib/api';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { OfflineIndicator, OfflineBanner } from './OfflineIndicator';
import { 
  LayoutDashboard, 
  ClipboardList, 
  FileCheck, 
  Users, 
  Settings, 
  LogOut,
  Menu,
  X,
  Shield,
  ChevronRight,
  KeyRound,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertCircle,
  BarChart3
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Abnahmen', href: '/inspections', icon: FileCheck },
  { name: 'Statistiken', href: '/statistics', icon: BarChart3 },
  { name: 'Checklisten', href: '/checklists', icon: ClipboardList },
];

const adminNavigation = [
  { name: 'Benutzer', href: '/users', icon: Users },
];

export function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const resetPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    if (newPassword.length < 6) {
      setPasswordError('Neues Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwörter stimmen nicht überein');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      await authApi.changePassword(currentPassword, newPassword);
      setPasswordSuccess(true);
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setPasswordLoading(false);
    }
  };

  const NavItem = ({ item }) => {
    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
    
    return (
      <Link
        to={item.href}
        onClick={() => setSidebarOpen(false)}
        className={cn(
          'flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 touch-target',
          isActive 
            ? 'bg-primary/10 text-primary border border-primary/20' 
            : 'text-muted-foreground hover:bg-accent hover:text-foreground'
        )}
        data-testid={`nav-${item.name.toLowerCase()}`}
      >
        <item.icon className="h-5 w-5" strokeWidth={1.5} />
        <span className="font-medium">{item.name}</span>
        {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-3">
            <img 
              src={user?.tenant_logo || "/firecheck-logo.png"} 
              alt={user?.tenant_name || "FireCheck"} 
              className="h-10 w-10 rounded-lg object-contain" 
            />
            <span className="font-heading font-bold text-lg">
              {user?.tenant_name || "FireCheck"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <OfflineIndicator />
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        'fixed top-0 left-0 z-50 h-full w-72 bg-card border-r border-border transition-transform duration-300 lg:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-20 border-b border-border">
            <img 
              src={user?.tenant_logo || "/firecheck-logo.png"} 
              alt={user?.tenant_name || "FireCheck"} 
              className="h-12 w-12 rounded-lg object-contain shadow-md" 
            />
            <div>
              <h1 className="font-heading font-bold text-xl">
                {user?.tenant_name || "FireCheck"}
              </h1>
              <p className="text-xs text-muted-foreground">
                {user?.tenant_name ? "Brandschutz-App" : "Brandschutz Wallis"}
              </p>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="space-y-1">
              {navigation.map((item) => (
                <NavItem key={item.name} item={item} />
              ))}
              
              {isAdmin() && (
                <>
                  <div className="px-4 py-3">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Administration
                    </p>
                  </div>
                  {adminNavigation.map((item) => (
                    <NavItem key={item.name} item={item} />
                  ))}
                </>
              )}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-semibold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
            <div className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2"
                onClick={() => {
                  resetPasswordForm();
                  setShowPasswordDialog(true);
                }}
                data-testid="change-password-button"
              >
                <KeyRound className="h-4 w-4" />
                Passwort ändern
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2 text-muted-foreground hover:text-red-500 hover:border-red-500/30"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn(
        'min-h-screen transition-all duration-300',
        'pt-16 lg:pt-0 lg:ml-72'
      )}>
        {/* Offline Banner */}
        <OfflineBanner />
        
        {/* Desktop header with offline indicator */}
        <div className="hidden lg:flex items-center justify-end px-8 py-4 border-b border-border/50">
          <OfflineIndicator />
        </div>
        
        <div className="p-4 md:p-8 lg:p-12">
          {children}
        </div>
      </main>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={(open) => {
        setShowPasswordDialog(open);
        if (!open) resetPasswordForm();
      }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              Passwort ändern
            </DialogTitle>
            <DialogDescription>
              Geben Sie Ihr aktuelles und neues Passwort ein.
            </DialogDescription>
          </DialogHeader>
          
          {passwordSuccess ? (
            <div className="py-2 space-y-3">
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <div className="flex items-center gap-2 text-green-500">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">Passwort geändert!</span>
                </div>
              </div>
              <Button 
                className="w-full" 
                onClick={() => setShowPasswordDialog(false)}
              >
                Fertig
              </Button>
            </div>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-3">
              {passwordError && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-destructive/10 text-red-500 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {passwordError}
                </div>
              )}
              
              <div className="space-y-1">
                <Label htmlFor="current-password" className="text-sm">Aktuelles Passwort</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Aktuelles Passwort"
                    required
                    className="pr-10 h-9"
                    data-testid="current-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full w-9"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="new-password" className="text-sm">Neues Passwort</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mind. 6 Zeichen"
                    required
                    className="pr-10 h-9"
                    data-testid="new-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full w-9"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="confirm-password" className="text-sm">Passwort bestätigen</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Passwort wiederholen"
                  required
                  className="h-9"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowPasswordDialog(false)}
                >
                  Abbrechen
                </Button>
                <Button 
                  type="submit"
                  className="flex-1"
                  disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                  data-testid="save-password-button"
                >
                  {passwordLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Speichern'
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
